import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQuery } from '@tanstack/react-query';
import {
  viewSpecificControllerListComplianceChecksOnPolicyQueryOptions,
  viewSpecificControllerListPolicyComplianceAlertsQueryOptions,
} from 'korint-sdk';
import { ShieldCheck } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion } from '../../components/ui/accordion';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { EntitySection } from './EntitySection';
import type { CheckGroup } from './types';
import { Center, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

export function ComplianceModule({ onUpdate }: { onUpdate?: (canGoNext: boolean) => void }) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();

  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: ['ACKNOWLEDGE_COMPLIANCE_ALERT'],
  });
  const canAcknowledge = can.ACKNOWLEDGE_COMPLIANCE_ALERT;

  const { data: checksRaw, isLoading: isChecksLoading } = useQuery({
    ...viewSpecificControllerListComplianceChecksOnPolicyQueryOptions(policy.id),
    select: data => data.data,
  });

  const checks = checksRaw ?? [];

  const targetGroups = useMemo(() => {
    const map = new Map<string, CheckGroup>();
    for (const check of checks) {
      const label = t(`compliance.targets.${check.target}`, { defaultValue: check.target });
      const group = map.get(check.target) ?? {
        target: check.target,
        targetLabel: label,
        checks: [],
      };
      group.checks.push(check);
      map.set(check.target, group);
    }
    return Array.from(map.values());
  }, [checks, t]);

  // For NEW_BUSINESS blocking: check if any unacknowledged alerts exist across all policy entities.
  const { data: policyAlerts, status: alertsStatus } = useQuery({
    ...viewSpecificControllerListPolicyComplianceAlertsQueryOptions(policy.id),
    enabled: checks.length > 0 && policy.branchType === 'NEW_BUSINESS',
    select: data => data.data.filter(a => !a.acknowledged),
  });

  const totalUnacknowledged = policyAlerts?.length ?? 0;

  useEffect(() => {
    if (policy.branchType !== 'NEW_BUSINESS') {
      onUpdate?.(true);
      return;
    }

    if (isChecksLoading) {
      onUpdate?.(false);
      return;
    }

    if (checks.length === 0) {
      onUpdate?.(true);
      return;
    }
    onUpdate?.(true);
  }, [
    policy.branchType,
    isChecksLoading,
    checks.length,
    alertsStatus,
    totalUnacknowledged,
    canAcknowledge,
    onUpdate,
  ]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <Stack gap={1}>
          <Title order={3} size="lg" className="text-secondary">
            {t('compliance.title')}
          </Title>
          <Text size="xs" tone="muted">
            {t('compliance.subtitle')}
          </Text>
        </Stack>
      </CardHeader>

      <CardContent className="pt-0">
        {isChecksLoading ? (
          <Stack gap={3} className="py-2">
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
          </Stack>
        ) : targetGroups.length === 0 ? (
          <Center className="gap-2 py-6">
            <ShieldCheck className="h-5 w-5 text-green-600" />
            <Text inline size="sm" weight="medium" className="text-green-700">
              {t('compliance.compliant')}
            </Text>
          </Center>
        ) : (
          <Stack asChild gap={2}>
            <Accordion type="multiple" defaultValue={targetGroups.map(g => g.target)}>
              {targetGroups.map(group => (
                <EntitySection
                  key={group.target}
                  group={group}
                  policyId={policy.id}
                  canAcknowledge={canAcknowledge}
                />
              ))}
            </Accordion>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
